'use client';
import { Area, CartesianGrid, XAxis, ComposedChart, Line, YAxis, Legend } from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';

import type { BucketData, Stack } from '@/types/Stack';
import { useData } from '@/hooks/useData';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
export const description = 'A step area chart';

let emptyData = [
    {
      bucket: '1h',
      data: [
    ]
    },
    {
      bucket: '15min',
      data: []
    },
    {
      bucket: '5min',
      data: []
    },
    {
      bucket: '30min',
      data: [],
    },
    {
      bucket: 'raw',
      data: []
    },
    {
        bucket: '24h',
        data: []
      },
  ] as BucketData[];

const chartConfig = {
  max: {
    label: 'Max',
    color: 'hsl(var(--chart-1))',
  },
  min: {
    label: 'min',
    color: 'hsl(var(--chart-2))',
  },
  mean: {
    label: 'mean',
    color: 'hsl(var(--chart-3))',
  },
  driveOffs: {
    label: 'driveOffs',
    color: 'hsl(var(--chart-5))',
  },
} satisfies ChartConfig;

export default function StackComponent({
  bucketData,
  isLoading
}: {
  bucketData: BucketData[] | undefined;
  isLoading: boolean;
}) {
  const { selectedDates, selectedStackDriveOffFilter, selectedLocation } = useData();
  if (bucketData === undefined) {
    bucketData = emptyData;
  }
  const options: Intl.DateTimeFormatOptions = selectedStackDriveOffFilter === '24h'
  ? { dateStyle: 'short' } // Use dateStyle if "24h"
  : { timeStyle: 'short' }; // Use timeStyle otherwise

  function formatter(localDate: string): string {
    const formattedDate = Intl.DateTimeFormat('en-US', options).format(new Date(localDate));

    return formattedDate;
  }

  // logic to allow graph to stay populated while changing dates
  emptyData = bucketData;
  const yMin = 0;
  const selectedBucket = bucketData.find(item => item.bucket === "raw");
  const yMax = selectedBucket && selectedBucket.data.length > 0
    ? Math.max(
        ...selectedBucket.data.flatMap(d => [
          d.StackSize ?? 0
        ])
      )
    : 0;

  const integerTicks = Array.from({ length: yMax - yMin + 1 }, (_, i) => yMin + i);
  return (
    <Card className="text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-gray-100 dark:border-slate-700 max-w-[95%]" >
      <CardHeader>
      <CardTitle>Stack / DriveOff Chart for <b>{selectedLocation?.displayName}</b></CardTitle>
        <CardDescription>
          Car Stack Data for {selectedDates[0].toDateString()} to {selectedDates[selectedDates.length - 1].toDateString()}
        </CardDescription>
      </CardHeader>
      <CardContent >

      {(isLoading || bucketData === undefined || bucketData[bucketData.findIndex(item => item.bucket === 'raw')].data.length === 0) && (
          <LoadingSpinner></LoadingSpinner>
      )}
      {(!isLoading && bucketData[bucketData.findIndex(item => item.bucket === 'raw')].data.length !== 0) && (
        <ChartContainer config={chartConfig}>
          <ComposedChart
            accessibilityLayer
            data={bucketData[bucketData.findIndex(item => item.bucket === selectedStackDriveOffFilter)].data.map((d) => ({
              ...d,
              StackSize_max: d.StackSize_max < 0 ? 0 : d.StackSize_max,
              StackSize_mean: d.StackSize_mean < 0 ? 0 : d.StackSize_mean,
              StackSize_min: d.StackSize_min < 0 ? 0 : d.StackSize_min,
            }))}
            margin={{
              bottom: 20
            }}
          >
            <Legend verticalAlign="top" height={36} iconType="square"  />
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="TimeStamp"
              tickLine={false}
              axisLine={false}
              angle={-35}
              textAnchor={'end'}
              fontSize={14}
              tickMargin={6}
              tickFormatter={(value) => formatter(value)}

            />
            <YAxis
                tickLine={false}
                axisLine={false}
                ticks={integerTicks} 
                domain={[yMin, yMax]} 
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent className="bg-white dark:bg-slate-800"/>}>
              </ChartTooltip>

            <Area
              dataKey="StackSize_max"
              type="step"
              fill="var(--color-max)"
              name="Maximum Stack"
              fillOpacity={1}
              stroke="var(--color-max)"
              dot={false}
            />
            <Area
              dataKey="StackSize_mean"
              type="step"
              name="Average Stack"
              fill="var(--color-mean)"
              fillOpacity={1}
              stroke="var(--color-mean)"
            />
            <Area
              dataKey="StackSize_min"
              name="Minimum Stack"
              type="step"
              fill="var(--color-min)"
              fillOpacity={1}
              stroke="var(--color-min)"
              dot={false}
            />
            <Line
              dataKey="DriveOffs"
              strokeWidth = {0}
              name="DriveOffs"
              type="natural"
              fill="var(--color-driveOffs)"
              stroke="var(--color-driveOffs)"
              dot={{
 fill:
                'var(--color-driveOffs)',
r: 10
              }}
              activeDot={{
                r: 15,
fill:
                'var(--color-driveOffs)'
              }}
            />
          </ComposedChart>
        </ChartContainer>
      )}
      </CardContent>
    </Card>

  );
}
